import { Box } from '@mui/material';
import { Formik, FormikErrors } from 'formik';
import sortBy from 'lodash/fp/sortBy';
import { memo, useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useQueryClient } from 'react-query';
import { toast } from 'react-toastify';

import {
    AirlineConfiguration,
    AirportConfiguration,
    AirportConfigurationType,
    KeyGetGetConfigurationsByType,
    PutSetConfigurationsResponse,
    useMutationPutSetConfigurations,
    useQueryGetGetConfigurationsByType,
} from '../../backend';
import { useBoolean } from '../../hooks/useBoolean';
import { errorToastConfig } from '../../utils/utils';
import { ConfigurationFieldsContainer } from '../AirportConfiguration/ConfigurationContent/ConfigurationFieldsContainer';
import { ConfigurationHeader } from '../AirportConfiguration/ConfigurationHeader/ConfigurationHeader';
import { FormPageFooter } from '../navigation/FormPageFooter/FormPageFooter';
import { validateConfiguration } from './ConfigurationPage.utils';
import { StyledForm } from './StyledForm.style';

export interface ConfigurationsPageFormValues {
    //TODO: check!
    //in ConfigurationFieldsLineContainer display null, if nullable
    configurations: AirportConfiguration[] | null;
}

export const configurationsPollingInterval = 30 * 1000;

export const ConfigurationPage = memo(function ConfigurationPage() {
    const queryClient = useQueryClient();
    const [search, setSearch] = useState<string>('');
    const [configurationType, setConfigurationType] = useState<AirportConfigurationType>(
        AirportConfigurationType.AIRLINES,
    );
    const { value: enableRefetch, setValue: setEnableRefetch } = useBoolean(true);

    const { data: configurations } = useQueryGetGetConfigurationsByType(
        {
            type: configurationType,
            includeNonActive: true,
        },
        {
            refetchInterval: configurationsPollingInterval,
            enabled: enableRefetch,
            onError: () => {
                toast(<FormattedMessage id={'resourcesForm.error.default'} />, errorToastConfig);
            },
            select: response => {
                if (configurationType === AirportConfigurationType.AIRLINES) {
                    return {
                        ...response,
                        data: sortConfigurations(response.data),
                    };
                } else {
                    return response;
                }
            },
            notifyOnChangeProps: ['data', 'error'],
        },
    );

    const { mutate, isLoading } = useMutationPutSetConfigurations({
        onError: e => {
            toast(e?.message, errorToastConfig);
        },
        onSuccess: (response: PutSetConfigurationsResponse) => {
            queryClient.setQueriesData(
                { queryKey: [KeyGetGetConfigurationsByType, { includeNonActive: true, type: configurationType }] },
                response,
            );
        },
    });

    const initialValues = useMemo(
        (): ConfigurationsPageFormValues => ({
            configurations,
        }),
        [configurations],
    );

    const handleSubmit = useCallback(
        (values: ConfigurationsPageFormValues) => {
            mutate({
                airportConfiguration: values.configurations,
            });
            setEnableRefetch(true);
        },
        [mutate, setEnableRefetch],
    );

    const handleValidate = useCallback((values: ConfigurationsPageFormValues) => {
        const errors: FormikErrors<ConfigurationsPageFormValues> = {};

        values.configurations?.forEach((c, index) => {
            validateConfiguration(index, c, errors, values);
        });

        return errors;
    }, []);

    return (
        <Box display="flex" flexDirection="column" alignItems="center" width="100%" height="100%">
            <ConfigurationHeader search={search} setSearch={setSearch} />
            <Formik initialValues={initialValues} onSubmit={handleSubmit} enableReinitialize validate={handleValidate}>
                {props => (
                    <StyledForm>
                        <ConfigurationFieldsContainer
                            search={search}
                            configurationType={configurationType}
                            setConfigurationType={setConfigurationType}
                        />
                        <FormPageFooter isLoading={isLoading} setEnableRefetch={setEnableRefetch} {...props} />
                    </StyledForm>
                )}
            </Formik>
        </Box>
    );
});

// Sort configurations, with the following rules:
// - Always show default value first
// - for airlines:
//      - Sort by code Iata
//      - Sort by code Icao
function sortConfigurations(configurations: AirportConfiguration[] | undefined) {
    // NOTE: Sorting is done is ascending order.
    // e.g. an impactOrder index of 0 means the impact is shown before impactOrder index of 1, and so on
    // e.g. an impact code including 'DJI' means the impact is shown after all other impacts (last items in sorted array)
    return sortBy<AirportConfiguration>(
        [
            i => !i.defaultConfig, // Always show default value first
            i => (i.content as AirlineConfiguration).codeIata, // Sort by code Iata
            i => (i.content as AirlineConfiguration).codeIcao, // Sort by code Icao
        ],
        configurations,
    );
}
